import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, onSnapshot, QuerySnapshot, DocumentData, orderBy, limit } from 'firebase/firestore';
import { getFailoverAwareDatabase, getFailoverAwareAuth } from '../config/firebaseWithFailover';
import { onAuthStateChanged } from 'firebase/auth';
import {
  getNotificationState,
  updatePageVisit,
  updateLoadVisit,
  markNotificationsAsRead,
  markNotificationDocsAsRead,
  markLoadNotificationsAsRead,
  cleanupReadNotifications,
} from '../services/notificationTrackingService';
import Logger from '../services/logger';
import {
  DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES,
} from '../constants/notificationTypes';
import {
  cleanupExpiredBellTrayNotifications,
} from '../services/notificationTrayRetention';
import {
  BROKER_SHIPPER_SIDEBAR_NOTIFICATION_TYPES,
  BrokerShipperSidebarPage,
  getNotificationTypesForSidebarPage,
  mergePageVisitsPreferNewer,
  normalizeNotificationCreatedAt,
} from '../constants/notificationSidebarAllocation';
import { useSidebarOpsNotificationHygiene } from './useSidebarOpsNotificationHygiene';
import { computeBrokerShipperIndicatorCounts } from '../services/bellSidebarIndicatorCounts';

interface Notification {
  id: string;
  type: string;
  read: boolean;
  createdAt?: Date;
  updatedAt?: unknown;
  message?: string;
  senderId?: string;
  senderName?: string;
  recipientId?: string;
  poNumber?: string;
  status?: string;
  requiresAction?: boolean;
  loadDetails?: unknown;
  shipperId?: string;
  brokerId?: string;
  carrierId?: string;
  loadId?: string;
  [key: string]: unknown;
}

interface NotificationCounts {
  purchaseOrders: number;
  shippingSchedule: number;
  driverUpdates: number;
  carrierPartners: number;
  payments: number;
  shipmentArchive: number;
  bellOnly: number;
  total: number;
}

type ShipperPageVisitKey = BrokerShipperSidebarPage | 'dashboard';

const EMPTY_COUNTS: NotificationCounts = {
  purchaseOrders: 0,
  shippingSchedule: 0,
  driverUpdates: 0,
  carrierPartners: 0,
  payments: 0,
  shipmentArchive: 0,
  bellOnly: 0,
  total: 0,
};

function mapNotificationDoc(docSnap: { id: string; data: () => DocumentData }): Notification {
  const data = docSnap.data();
  return {
    ...data,
    id: docSnap.id,
    type: (data.type as string) || 'unknown',
    read: data.read === true,
    createdAt: normalizeNotificationCreatedAt(data.createdAt),
  };
}

export const useEnhancedShipperNotifications = () => {
  const [user, setUser] = useState<{ uid: string } | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationState, setNotificationState] = useState<{
    pageVisits?: Record<string, Date | null>;
    loadVisits?: Record<string, Date | null>;
    lastNotificationCheck?: Date | null;
  } | null>(null);
  const [counts, setCounts] = useState<NotificationCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const notificationsRef = useRef<Notification[]>([]);
  const poActiveMap = useSidebarOpsNotificationHygiene(notifications, user?.uid, 'shipperId');

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  useEffect(() => {
    const auth = getFailoverAwareAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser ? { uid: firebaseUser.uid } : null);
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setNotificationState(null);
      setCounts(EMPTY_COUNTS);
      return;
    }

    const uid = user.uid;
    let cancelled = false;
    let shipperUnsubscribe: (() => void) | null = null;
    let userUnsubscribe: (() => void) | null = null;

    const setupNotifications = async () => {
      setLoading(true);
      const initialState = await getNotificationState(uid);
      if (cancelled) return;

      setNotificationState((prev) => {
        if (!prev) return initialState;
        return {
          ...initialState,
          ...prev,
          pageVisits: mergePageVisitsPreferNewer(prev.pageVisits, initialState.pageVisits),
          loadVisits: { ...(initialState.loadVisits || {}), ...(prev.loadVisits || {}) },
        };
      });

      cleanupReadNotifications(uid).catch((err) =>
        Logger.warn('Failed to cleanup old notifications:', { err })
      );
      cleanupExpiredBellTrayNotifications(getFailoverAwareDatabase(), uid, [
        'shipperId',
        'userId',
      ]).catch((err) => Logger.warn('Failed to cleanup expired tray notifications:', { err }));

      const db = getFailoverAwareDatabase();

      const shipperQuery = query(
        collection(db, 'notifications'),
        where('shipperId', '==', uid),
        where('read', '==', false),
        orderBy('createdAt', 'desc'),
        limit(100)
      );

      const userQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', uid),
        where('read', '==', false),
        orderBy('createdAt', 'desc'),
        limit(100)
      );

      let shipperNotifications: Notification[] = [];
      let userNotifications: Notification[] = [];

      const processNotifications = () => {
        if (cancelled) return;
        const allNotifications = [...shipperNotifications, ...userNotifications];
        const uniqueNotifications = allNotifications.filter(
          (notification, index, self) => self.findIndex((n) => n.id === notification.id) === index
        );
        setNotifications(uniqueNotifications);
        setLoading(false);
      };

      shipperUnsubscribe = onSnapshot(
        shipperQuery,
        (snapshot: QuerySnapshot<DocumentData>) => {
          shipperNotifications = snapshot.docs.map(mapNotificationDoc);
          processNotifications();
        },
        (error) => {
          Logger.error(
            '[useEnhancedShipperNotifications] Shipper query error:',
            error instanceof Error ? error : new Error(String(error)),
            { error }
          );
          if (!cancelled) setLoading(false);
        }
      );

      userUnsubscribe = onSnapshot(
        userQuery,
        (snapshot: QuerySnapshot<DocumentData>) => {
          userNotifications = snapshot.docs.map(mapNotificationDoc);
          processNotifications();
        },
        (error) => {
          Logger.error(
            '[useEnhancedShipperNotifications] User query error:',
            error instanceof Error ? error : new Error(String(error)),
            { error }
          );
          if (!cancelled) setLoading(false);
        }
      );
    };

    setupNotifications();

    return () => {
      cancelled = true;
      if (shipperUnsubscribe) shipperUnsubscribe();
      if (userUnsubscribe) userUnsubscribe();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!notificationState || notifications.length === 0) {
      setCounts(EMPTY_COUNTS);
      return;
    }

    setCounts(
      computeBrokerShipperIndicatorCounts(
        'shipper',
        notifications,
        notificationState.pageVisits || {},
        poActiveMap
      )
    );
  }, [notifications, notificationState, poActiveMap]);

  const markPageVisited = useCallback(
    async (page: ShipperPageVisitKey) => {
      if (!user?.uid) return;

      const now = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      setNotificationState((prev) => {
        if (!prev) {
          return {
            pageVisits: {
              purchaseOrders: thirtyDaysAgo,
              shippingSchedule: thirtyDaysAgo,
              driverUpdates: thirtyDaysAgo,
              carrierPartners: thirtyDaysAgo,
              payments: thirtyDaysAgo,
              shipmentArchive: thirtyDaysAgo,
              dashboard: thirtyDaysAgo,
              [page]: now,
            },
            loadVisits: {},
            lastNotificationCheck: now,
          };
        }
        return {
          ...prev,
          pageVisits: {
            ...(prev.pageVisits || {}),
            [page]: now,
          },
        };
      });

      // Persist visit first — sidebar dots key off pageVisits
      await updatePageVisit(user.uid, page);

      if (page !== 'dashboard') {
        const typesForPage = new Set(getNotificationTypesForSidebarPage(page));
        const ids = notificationsRef.current
          .filter((n) => typesForPage.has(n.type) && n.read !== true)
          .map((n) => n.id);
        await markNotificationDocsAsRead(ids);
        await markNotificationsAsRead(user.uid, page);
      }
    },
    [user?.uid]
  );

  const markLoadVisited = useCallback(
    async (loadId: string) => {
      if (!user?.uid) return;
      await updateLoadVisit(user.uid, loadId);
      await markLoadNotificationsAsRead(user.uid, loadId);
      setNotificationState((prev) => ({
        ...prev,
        pageVisits: prev?.pageVisits || {},
        loadVisits: {
          ...(prev?.loadVisits || {}),
          [loadId]: new Date(),
        },
      }));
    },
    [user?.uid]
  );

  const hasBellNotifications = counts.bellOnly > 0;

  return { counts, hasBellNotifications, markPageVisited, markLoadVisited, loading };
};

export const SHIPPER_NOTIFICATION_ALLOCATION = {
  SIDEBAR_DOTS: BROKER_SHIPPER_SIDEBAR_NOTIFICATION_TYPES,
  DASHBOARD_CARDS_ONLY: [...DASHBOARD_ACTION_REQUIRED_NOTIFICATION_TYPES],
};
